/**
 * PATH: packages/email-hub/src/feature/reader-header/placements/PlacementRow.tsx
 * FILE_ID: EMAILHUB-READERHEADER-PLACEMENTS-ROW-001
 * PURPOSE: One Placement Manager row — metadata chrome (source badge, review
 *   state, org, creator/visibility/last-change when available), designate
 *   primary, and the availability edge states (Locked, stale, changed
 *   elsewhere) around the shared AllocationChip verb surface.
 * ROLE IN ARCHITECTURE: Presentational leaf of the Placement Manager.
 * MAIN EXPORTS: PlacementRow.
 * NON-RESPONSIBILITIES: Change lane / move board / accept / reject / revoke on
 *   healthy rows — @quotech/kanban-host AllocationChip owns those verbs (reuse
 *   mandate; its supersedes-learning gate must not be forked).
 * NOTES FOR FUTURE AI: Locked rows must NEVER render board/lane/card names —
 *   the RBAC gate nulled them; do not "improve" the copy with ids either.
 */
import { AlertTriangle, Lock, RefreshCw, Star, Trash2 } from 'lucide-react'
import { AllocationChip, type getSamKanbanApi } from '@quotech/kanban-host'
import type { EmailAllocationSummary } from '@quotech/tenant-shared/email/api'

import { cn } from '../../../_internal/lib/utils'
import {
  placementAvailability,
  placementExtras,
  placementSourceBadge,
} from './placementModel'

const BADGE_TONE: Record<string, string> = {
  manual: 'bg-slate-500/15 text-slate-700 dark:text-slate-300',
  rule: 'bg-violet-500/15 text-violet-700 dark:text-violet-300',
  ai: 'bg-sky-500/15 text-sky-700 dark:text-sky-300',
  pending: 'bg-amber-500/15 text-amber-800 dark:text-amber-300',
}

export interface PlacementRowProps {
  emailId: string
  placement: EmailAllocationSummary
  index: number
  api: ReturnType<typeof getSamKanbanApi>
  orgId: number | null
  organisationName?: string | null
  isPrimary: boolean
  /** True when THIS row is the operator's manual designation. */
  isDesignated: boolean
  /** Version token changed since the manager opened (concurrent edit). */
  changedElsewhere: boolean
  busy: boolean
  onOpenCard?: (cardId: string) => void
  onOpenBoard?: (placement: EmailAllocationSummary) => void
  onChanged?: () => void
  onDesignatePrimary: (placement: EmailAllocationSummary) => void
  onRemoveStale: (placement: EmailAllocationSummary) => void
  onRefresh: () => void
}

function formatLastChange(iso: string | null): string | null {
  if (!iso) return null
  const parsed = new Date(iso)
  if (Number.isNaN(parsed.getTime())) return null
  return parsed.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  })
}

/** Metadata line under the verb chip: badge, review state, org, extras. */
function PlacementMeta({
  placement,
  organisationName,
}: Pick<PlacementRowProps, 'placement' | 'organisationName'>) {
  const badge = placementSourceBadge(placement.source)
  const extras = placementExtras(placement)
  const lastChange = formatLastChange(extras.updatedAt)
  return (
    <div
      className="mt-1 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] text-muted-foreground"
      data-testid="placement-meta"
    >
      <span
        className={cn(
          'rounded-full px-1.5 py-px font-medium',
          BADGE_TONE[badge.tone],
        )}
        data-testid="placement-source-badge"
      >
        {badge.label}
      </span>
      {placement.needs_review && (
        <span className="rounded-full bg-amber-100 px-1.5 py-px font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
          Review pending
        </span>
      )}
      {organisationName && <span className="truncate">{organisationName}</span>}
      {extras.visibility && <span>{extras.visibility}</span>}
      {extras.creatorName && <span>by {extras.creatorName}</span>}
      {lastChange && <span>changed {lastChange}</span>}
    </div>
  )
}

export function PlacementRow({
  emailId,
  placement,
  index,
  api,
  orgId,
  organisationName,
  isPrimary,
  isDesignated,
  changedElsewhere,
  busy,
  onOpenCard,
  onOpenBoard,
  onChanged,
  onDesignatePrimary,
  onRemoveStale,
  onRefresh,
}: PlacementRowProps) {
  const availability = placementAvailability(placement)

  return (
    <li
      className="rounded-lg border border-border/70 bg-background p-2"
      data-testid={`placement-row-${placement.allocation_id}`}
      data-placement-availability={availability}
    >
      {changedElsewhere && (
        <div
          className="mb-1.5 flex items-center justify-between gap-2 rounded-md bg-amber-50 px-2 py-1 text-[10px] text-amber-800 dark:bg-amber-900/30 dark:text-amber-200"
          data-testid="placement-changed-elsewhere"
        >
          <span className="inline-flex items-center gap-1">
            <AlertTriangle className="h-3 w-3 shrink-0" />
            Changed elsewhere
          </span>
          <button
            type="button"
            onClick={onRefresh}
            className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-medium hover:bg-amber-100 dark:hover:bg-amber-900/50 pointer-coarse:min-h-11"
          >
            <RefreshCw className="h-3 w-3" />
            Refresh
          </button>
        </div>
      )}
      <div className="flex min-w-0 items-start gap-2">
        <span className="mt-1 w-5 shrink-0 text-[10px] tabular-nums text-muted-foreground">
          {index + 1}
        </span>
        <div className="min-w-0 flex-1">
          {availability === 'locked' ? (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground"
              data-testid="placement-locked"
              title="You do not have access to this placement"
            >
              <Lock className="h-3 w-3" />
              Locked
            </span>
          ) : availability === 'stale' ? (
            <span
              className="inline-flex items-center gap-1 text-[11px] text-muted-foreground line-through"
              data-testid="placement-stale"
            >
              {placement.board_name} · {placement.lane_name} — unavailable
            </span>
          ) : (
            <AllocationChip
              emailId={emailId}
              summary={placement}
              api={api}
              orgId={orgId}
              onOpenCard={onOpenCard}
              onOpenBoard={onOpenBoard ? () => onOpenBoard(placement) : undefined}
              onChanged={onChanged}
              // Manager dialog has room — wrap long board › lane crumbs
              // instead of crushing them into an unreadable truncate
              // (BUG-2026-08-03-PLACEMENT-MGR-CHIP-SQUASH).
              wrapLabel
            />
          )}
          {availability !== 'locked' && (
            <PlacementMeta
              placement={placement}
              organisationName={organisationName}
            />
          )}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1 self-start">
          {isPrimary && (
            <span className="rounded-full bg-muted px-2 py-0.5 text-[9px] uppercase tracking-wide text-muted-foreground">
              Primary
            </span>
          )}
          {availability === 'ok' && (
            <button
              type="button"
              onClick={() => onDesignatePrimary(placement)}
              disabled={busy}
              className={cn(
                'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md hover:bg-accent pointer-coarse:h-11 pointer-coarse:w-11',
                isDesignated ? 'text-amber-500' : 'text-muted-foreground',
              )}
              aria-pressed={isDesignated}
              aria-label={
                isDesignated ? 'Remove primary designation' : 'Designate primary'
              }
              title={
                isDesignated ? 'Remove primary designation' : 'Designate primary'
              }
              data-testid={`placement-designate-${placement.allocation_id}`}
            >
              <Star
                className="h-3.5 w-3.5"
                fill={isDesignated ? 'currentColor' : 'none'}
              />
            </button>
          )}
          {availability === 'stale' && (
            <button
              type="button"
              onClick={() => onRemoveStale(placement)}
              disabled={busy}
              className="inline-flex shrink-0 items-center gap-1 rounded-md border border-border px-2 py-1 text-[10px] text-rose-600 hover:bg-rose-50 disabled:opacity-50 dark:text-rose-400 dark:hover:bg-rose-950/40 pointer-coarse:min-h-11"
              data-testid={`placement-remove-stale-${placement.allocation_id}`}
            >
              <Trash2 className="h-3 w-3" />
              Remove stale placement
            </button>
          )}
        </div>
      </div>
    </li>
  )
}
